import { readdir } from 'node:fs/promises';
import { join, relative } from 'node:path';
import type { ProjectStructure } from './context-types.js';
import { isSecretPath } from './important-files.js';

export interface StructureScannerOptions {
  maxDepth?: number;
  maxFiles?: number;
  ignore: (relativePath: string) => boolean;
}

export async function scanProjectStructure(
  workspaceRoot: string,
  options: StructureScannerOptions,
): Promise<ProjectStructure> {
  const maxDepth = options.maxDepth ?? 4;
  const maxFiles = options.maxFiles ?? 100;
  const files: string[] = [];
  const directories: string[] = [];
  let truncated = false;

  async function walk(current: string, depth: number): Promise<void> {
    if (depth > maxDepth || files.length >= maxFiles) {
      truncated = true;
      return;
    }
    let entries;
    try {
      entries = await readdir(current, { withFileTypes: true });
    } catch {
      return;
    }
    entries.sort((a, b) => a.name.localeCompare(b.name));
    for (const entry of entries) {
      const abs = join(current, entry.name);
      const rel = relative(workspaceRoot, abs).replace(/\\/g, '/');
      if (!rel || options.ignore(rel) || isSecretPath(rel)) continue;
      if (entry.isDirectory()) {
        directories.push(rel);
        await walk(abs, depth + 1);
      } else {
        if (files.length >= maxFiles) {
          truncated = true;
          break;
        }
        files.push(rel);
      }
    }
  }

  await walk(workspaceRoot, 0);
  return { root: workspaceRoot, files, directories, maxDepth, maxFiles, truncated };
}
